import { useEffect, useMemo, useState } from "react";
import { properties, states } from "../data/properties";
import TableWithSlider from "../Components/TableWithSlider";

const stateFilterLabel = (state) => (state === "All States" ? "All Properties" : `${state} Properties`);
const parsePercent = (value) => Number(String(value || "").replace(/[^0-9.-]/g, "")) || 0;

function Properties() {
  const [selectedState, setSelectedState] = useState("All States");
  const [sortConfig, setSortConfig] = useState({ key: "capRate", direction: "desc" });
  const propertiesWithRouteIds = useMemo(() => properties, []);

  useEffect(() => {
    const preferred = sessionStorage.getItem("preferredState");
    if (preferred && states.includes(preferred)) {
      setSelectedState(preferred);
    }
    sessionStorage.removeItem("preferredState");
  }, []);

  const filtered = useMemo(() => {
    return propertiesWithRouteIds.filter((property) => {
      return selectedState === "All States" || property.state === selectedState;
    });
  }, [propertiesWithRouteIds, selectedState]);
  const sorted = useMemo(() => {
    return [...filtered].sort((left, right) => {
      const leftValue = parsePercent(left.capRate);
      const rightValue = parsePercent(right.capRate);

      return sortConfig.direction === "asc" ? leftValue - rightValue : rightValue - leftValue;
    });
  }, [filtered, sortConfig]);

  const handleCapRateSort = () => {
    setSortConfig((current) => {
      return {
        key: "capRate",
        direction: current.direction === "asc" ? "desc" : "asc",
      };
    });
  };

  const renderCapRateHeader = () => {
    const indicator = sortConfig.direction === "asc" ? "▲" : "▼";

    return (
      <button
        type="button"
        className="flex items-center gap-2 text-xs uppercase tracking-[0.12em]"
        onClick={handleCapRateSort}
      >
        <span>CAP Rate</span>
        <span className="text-[10px] text-cream-200/80">{indicator}</span>
      </button>
    );
  };

  return (
    <div className="section-wrap space-y-8 py-10 lg:py-14">
      <section className="premium-panel p-7 sm:p-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian-600">Properties</p>
        <h1 className="mt-3 font-serif text-5xl leading-tight text-obsidian-950">
          Investor-Focused Net-Lease Opportunities
        </h1>
        <p className="mt-4 max-w-4xl text-sm leading-7 text-obsidian-700">
          Browse the full property list in table format and narrow it using a single state dropdown,
          just like the Cap Rate Experts listing experience your client referenced.
        </p>
      </section>

      <section className="premium-panel max-w-sm p-5">
        <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-obsidian-600">
          Select Property State
          <select
            value={selectedState}
            onChange={(event) => setSelectedState(event.target.value)}
            className="rounded-xl border border-obsidian-300 bg-cream-50 px-3 py-3 text-sm font-medium normal-case tracking-normal text-obsidian-900"
          >
            {states.map((state) => (
              <option key={state} value={state}>
                {stateFilterLabel(state)}
              </option>
            ))}
          </select>
        </label>
      </section>

      {filtered.length ? (
        <TableWithSlider>
          <table className="min-w-[1200px] w-full text-left">
            <thead className="bg-obsidian-900 text-cream-50">
              <tr>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.12em]">Asking Price</th>
                <th className="px-4 py-3">{renderCapRateHeader()}</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.12em]">Tenant</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.12em]">Net Rent</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.12em]">Expires</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.12em]">Rent Type</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.12em]">Landlord</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.12em]">Built</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.12em]">State</th>
                <th className="px-4 py-3 text-xs uppercase tracking-[0.12em]">Property ID</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((property) => (
                <tr key={property.id} className="border-t border-obsidian-100 text-sm text-obsidian-800">
                  <td className="px-4 py-3">{property.askingPrice || property.price}</td>
                  <td className="px-4 py-3">{property.capRate}</td>
                  <td className="px-4 py-3">{property.tenant}</td>
                  <td className="px-4 py-3">{property.netRent}</td>
                  <td className="px-4 py-3">{property.expires || property.leaseTerm}</td>
                  <td className="px-4 py-3">{property.rentType}</td>
                  <td className="px-4 py-3">{property.landlord}</td>
                  <td className="px-4 py-3">{property.built || property.yearBuilt}</td>
                  <td className="px-4 py-3">{property.state}</td>
                  <td className="px-4 py-3">{property.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithSlider>
      ) : (
        <section className="premium-panel p-8 text-center">
          <h2 className="font-serif text-4xl text-obsidian-950">No Properties Matched</h2>
          <p className="mt-3 text-sm text-obsidian-700">
            Adjust your state search or filter values and try again.
          </p>
        </section>
      )}
    </div>
  );
}

export default Properties;
